import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import macbookImage from '@/assets/macbook-repair.jpg';

const macBookServiceSchema = generateServiceSchema({
  name: 'MacBook Repair Service in Patna, Bihar',
  description: 'Professional MacBook repair services in Bihar. Screen replacement, battery replacement, keyboard repair, logic board repair for MacBook Air and MacBook Pro.',
  url: 'https://ifixappleservicecenter.in/macbook-service',
  ratingValue: 4.9,
  reviewCount: 320,
});

const macBookBreadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'MacBook Service', url: 'https://ifixappleservicecenter.in/macbook-service' },
]);

const macbookFaqs = [
  {
    question: 'How much does MacBook screen replacement cost in Patna?',
    answer: 'MacBook screen replacement costs vary by model and display type. Contact us at +91 97986 53591 with your model number for an accurate quote.',
  },
  {
    question: 'Can you repair M1/M2/M3 MacBook chips?',
    answer: 'Yes, we have expertise in repairing Apple Silicon MacBooks including M1, M2, M3, and M4 chips. We can diagnose and repair logic board issues, though chip-level repairs may require component replacement.',
  },
  {
    question: 'How long does MacBook keyboard replacement take?',
    answer: 'MacBook keyboard replacement typically takes 2-4 hours depending on the model. Butterfly keyboard MacBooks may take longer due to the complex replacement process.',
  },
  {
    question: 'Do you offer data recovery for damaged MacBooks?',
    answer: 'Yes, we offer data recovery services for water-damaged, crashed, or non-booting MacBooks. Success rates vary based on damage extent. We always attempt data backup before any repair.',
  },
  {
    question: 'Is there pickup and delivery service for MacBook repairs in Bihar?',
    answer: 'We offer pickup and delivery services within Patna city limits for MacBook repairs. For customers outside Patna, we can arrange courier-based service with careful packaging.',
  },
];



const MacBookService = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const services = [
    'Screen Replacement',
    'Battery Replacement',
    'Logic Board Repair',
    'Keyboard Replacement',
    'Water Damage Repair',
    'Overheating Issue',
    'Network Issue',
    'Stuck on Logo',
    'Charging Issue',
    'Storage Upgrade',
    'Touchpad Not Working',
    'Speaker Replacement',
  ];

  const models = [
    'MacBook Air A1466', 'MacBook Air A1369', 'MacBook Air A1932',
    'MacBook Air M1', 'MacBook Air M2', 'MacBook Air M3',
    'MacBook Pro 13" A1502', 'MacBook Pro 13" A1708', 'MacBook Pro 13" A1989',
    'MacBook Pro 14" M1 Pro', 'MacBook Pro 14" M2 Pro', 'MacBook Pro 14" M3 Pro', 'MacBook Pro 14" M4',
    'MacBook Pro 15" A1398', 'MacBook Pro 15" A1707', 'MacBook Pro 15" A1990',
    'MacBook Pro 16" M1 Pro', 'MacBook Pro 16" M1 Max', 'MacBook Pro 16" M2 Pro', 'MacBook Pro 16" M2 Max',
    'MacBook Pro 16" M3 Pro', 'MacBook Pro 16" M3 Max', 'MacBook Pro 16" M4 Pro', 'MacBook Pro 16" M4 Max',
  ];

  return (
    <Layout>
      <SEOHead
        title="MacBook Service Center Patna Bihar | MacBook Repair Near Me - iFix"
        description="Best MacBook service center near me in Patna Bihar. Professional Mac service center for screen, battery, keyboard & logic board repairs for MacBook Air & Pro. Apple repair store near me with certified technicians. Call now!"
        url="https://ifixappleservicecenter.in/macbook-service"
      />
      <JsonLd data={macBookServiceSchema} />
      <JsonLd data={macBookBreadcrumbSchema} />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={macbookImage}
            alt="MacBook repair service center in Patna Bihar - screen keyboard battery logic board repair"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[{ name: 'MacBook Service', path: '/macbook-service' }]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            MacBook Service Center
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Looking for MacBook service center near me? iFix is your trusted Mac service center in Bihar with expert technicians for MacBook Air and MacBook Pro repairs, 
            from screen replacement to logic board repairs. Visit our Apple repair centre near me for same-day service.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book MacBook Repair
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20MacBook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            MacBook Repair Services
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Complete MacBook service center solutions for all hardware and software issues. Expert Mac service center near me for MacBook Air and MacBook Pro at our Apple service center in Patna.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service, index) => (
              <div
                key={service}
                className="group flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1 transition-all duration-300 opacity-0 animate-fade-in cursor-pointer"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-foreground group-hover:text-primary transition-colors duration-300">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Supported MacBook Models
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We service all MacBook models from MacBook Air A1466 to the latest M4 range.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {models.map((model, index) => (
              <span
                key={model}
                className="px-4 py-2 bg-background rounded-full text-sm font-medium text-foreground border border-border hover:border-primary/50 hover:bg-primary/5 hover:text-primary hover:scale-105 hover:shadow-md transition-all duration-300 cursor-pointer opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'forwards' }}
              >
                {model}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8 text-center">
            Related Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              to="/blog/macbook-repair-service-bihar"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                MacBook Repair Guide
              </h3>
              <p className="text-sm text-muted-foreground">
                Complete guide to MacBook repair services.
              </p>
            </Link>
            <Link
              to="/iphone-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Expert iPhone screen and battery repair.
              </p>
            </Link>
            <Link
              to="/ipad-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPad Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Professional iPad screen and charging repair.
              </p>
            </Link>
          </div>
          
          {/* Cross-linking */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Explore more Apple repair services:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/iphone-service" className="text-primary hover:underline font-medium">
                iPhone Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/ipad-service" className="text-primary hover:underline font-medium">
                iPad Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/watch-service" className="text-primary hover:underline font-medium">
                Apple Watch Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog" className="text-primary hover:underline font-medium">
                All Repair Guides
              </Link>
              <span className="text-border">•</span>
              <Link to="/franchise" className="text-primary hover:underline font-medium">
                Franchise Opportunity
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="MacBook Repair FAQs"
        subtitle="Common questions about our MacBook repair services in Bihar"
        faqs={macbookFaqs}
      />

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Need MacBook Repair?
          </h2>
          <p className="text-background/70 mb-8 max-w-xl mx-auto">
            Get your MacBook repaired by certified experts. Professional service with genuine parts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default MacBookService;
